import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useCallback, useMemo } from "react";
import { Button } from "reactstrap";
import { useIntl } from "react-intl";
import yourLogoHere from "../../../assets/yourLogoHere.png";
import { CompanyPayload } from "../../../cores/company/interfaces/api/get-companies";
import { useGlobalCore } from "../../../cores/globals";
import { useMeCore } from "../../../cores/me";
import { useRouterCore } from "../../../cores/router";
import { Company } from "../../../cores/me/interfaces/api/get-meCompany";
import { PlanBadge } from "./PlanBadge";
import { ResellerBadge } from "./ResellerBadge";
import { RoleBadges } from "./RoleBadge";
import "../../styles/CompaniesTable.scss";

interface Props {
  companies: CompanyPayload[];
  onEditCompany: (companyId: string) => void;
  onDeleteCompany: (company: Company) => void;
  selectedIds: string[];
  onToggleCompany: (companyId: string) => void;
  onToggleAllCompanies: () => void;
}

export default function CompanyTable(props: Props) {
  const {
    companies,
    onEditCompany,
    onDeleteCompany,
    selectedIds,
    onToggleCompany,
    onToggleAllCompanies,
  } = props;
  const intl = useIntl();

  const { reset } = useGlobalCore();
  const { selectCompany, company: meCompany, user, isAdmin, isReseller } = useMeCore();
  const { goToSocial } = useRouterCore();

  const isAllowedToEdit = useCallback(
    (role: any) =>
      Boolean(
        role?.owner ||
          role?.admin ||
          user?.userRole?.admin ||
          user?.userRole?.reseller
      ),
    [user]
  );

  const handleSelectCompany = useCallback(
    (company: Company, role?: any) => {
      if (role || isAdmin || isReseller) {
        reset();
        selectCompany(company.id);
        goToSocial();
      }
    },
    [goToSocial, isAdmin, isReseller, reset, selectCompany]
  );

  const selectableCompanyIds = useMemo(
    () =>
      companies
        .filter(({ company }) => Boolean(user?.userRole?.admin) && meCompany?.id !== company.id)
        .map(({ company }) => company.id),
    [companies, meCompany?.id, user?.userRole?.admin]
  );

  const allSelected =
    selectableCompanyIds.length > 0 &&
    selectableCompanyIds.every((companyId) => selectedIds.includes(companyId));

  return (
    <div className="companiesTableWrapper">
      <table className="companiesTable">
        <thead>
          <tr>
            <th className="companiesTable__th companiesTable__th--checkbox">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAllCompanies}
                aria-label={intl.formatMessage({
                  id: "app.companies.bulk.selectAll",
                  defaultMessage: "Seleccionar todas las empresas visibles",
                })}
              />
            </th>
            <th className="companiesTable__th companiesTable__th--name">
              {intl.formatMessage({ id: "app.company.title", defaultMessage: "Empresa" })}
            </th>
            <th className="companiesTable__th">Plan</th>
            <th className="companiesTable__th">Reseller</th>
            <th className="companiesTable__th">Owner</th>
            <th className="companiesTable__th companiesTable__th--actions">
              {intl.formatMessage({ id: "app.table.actions", defaultMessage: "Acciones" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {companies.map(({ company, role, plan, owner }) => {
            const isSelected = meCompany?.id === company.id;
            const canEdit = isAllowedToEdit(role);
            const canDelete = user?.userRole?.admin && !isSelected;
            const canSelect = !isSelected && Boolean(role || isAdmin || isReseller);

            const companyLogo = !company?.avatarUrl ? yourLogoHere : company.avatarUrl;

            return (
              <tr
                key={company.id}
                className={cn("companiesTable__tr", { "companiesTable__tr--selected": isSelected })}
              >
                <td className="companiesTable__td companiesTable__td--checkbox">
                  {canDelete ? (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(company.id)}
                      onChange={() => onToggleCompany(company.id)}
                      aria-label={intl.formatMessage(
                        {
                          id: "app.companies.bulk.selectOne",
                          defaultMessage: "Seleccionar empresa {name}",
                        },
                        { name: company.name }
                      )}
                    />
                  ) : null}
                </td>
                <td className="companiesTable__td companiesTable__td--name">
                  <div className="companiesTable__nameCell">
                    <img
                      src={companyLogo}
                      alt={company.name}
                      className="companiesTable__avatar"
                    />
                    <div className="companiesTable__nameMeta">
                      <div className="companiesTable__companyName">{company.name}</div>
                      {role ? (
                        <div className="companiesTable__roleBadges">
                          <RoleBadges roles={role} id={company.id} />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </td>

                <td className="companiesTable__td">
                  {plan ? (
                    <PlanBadge plan={plan} show />
                  ) : (
                    <FontAwesomeIcon
                      className="text-danger"
                      size="lg"
                      icon={["fas", "exclamation-triangle"]}
                    />
                  )}
                </td>

                <td className="companiesTable__td">
                  {company.reseller ? <ResellerBadge reseller={company.reseller} show /> : "-"}
                </td>

                <td className="companiesTable__td">
                  {owner ? `${owner.lastName} ${owner.firstName}` : "-"}
                </td>

                <td className="companiesTable__td companiesTable__td--actions">
                  <div className="companiesTable__actions">
                    {canSelect && (
                      <Button
                        color="primary"
                        size="sm"
                        className="companiesTable__btn"
                        onClick={() => handleSelectCompany(company, role)}
                      >
                        {intl.formatMessage({ id: "app.select.placeholder", defaultMessage: "Seleccionar" })}
                      </Button>
                    )}

                    {canEdit && (
                      <Button
                        color="secondary"
                        size="sm"
                        className="companiesTable__btn"
                        onClick={() => onEditCompany(company.id)}
                      >
                        {intl.formatMessage({ id: "app.adminCompanies.edit", defaultMessage: "Editar" })}
                      </Button>
                    )}

                    {canDelete && (
                      <Button
                        color="danger"
                        size="sm"
                        outline
                        className="companiesTable__btn companiesTable__btn--danger"
                        onClick={() => onDeleteCompany(company)}
                      >
                        <FontAwesomeIcon
                          icon={["fas", "trash"]}
                          className="mr-2"
                          size="1x"
                        />
                        {intl.formatMessage({ id: "app.adminCompanies.delete", defaultMessage: "Eliminar" })}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

